import React, { useEffect, useMemo, useState } from 'react';

import { Typography, Grid } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { PrintOptions } from 'components/_dialogs/PrintOptionsDialog/PrintOptionsDialog';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import { ActionInstanceStepComment } from 'config/api/actionInstanceStep/actionInstanceStep.types';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance, InstanceComment } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import print_template_messages from 'messages/print_template_messages';
import userDataStorage from 'storages/userDataStorage';

import useStyles from './ActionLibraryInstancePrintTemplate.styles';

type Props = {
  instance: ActionLibraryInstance;
  options?: PrintOptions;
};

const ActionLibraryInstancePrintTemplate: React.FC<Props> = ({ instance, options }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const currentUser = userDataStorage.get();

  const [instanceComments, setInstanceComments] = useState<InstanceComment[]>([]);
  const [stepComments, setStepComments] = useState<Record<number, ActionInstanceStepComment[]>>({});

  // Default to all true if no options provided (backward compatibility)
  const showTitle = options?.title ?? true;
  const showDescription = options?.description ?? true;
  const showMetadata = options?.metadata ?? true;
  const showSpecialInstruction = options?.specialInstruction ?? true;
  const showInstanceComments = options?.instanceComments ?? true;
  const showSteps = options?.steps ?? true;
  const showStepComments = options?.stepComments ?? true;

  useEffect(() => {
    if (!showInstanceComments && !showStepComments) return;

    const fetchComments = async () => {
      try {
        // Fetch instance comments
        if (showInstanceComments) {
          const { data: instanceCommentsData } = await actionLibraryInstancesApi.getInstanceComments(instance.id);
          setInstanceComments(instanceCommentsData.results);
        }

        // Fetch step comments
        if (showStepComments && instance.steps) {
          const stepCommentsPromises = instance.steps.map(step =>
            actionInstanceStepApi.getComments(step.id).then(res => ({ stepId: step.id, comments: res.data.results })),
          );
          const stepCommentsResults = await Promise.all(stepCommentsPromises);
          const stepCommentsMap = stepCommentsResults.reduce((acc, { stepId, comments }) => {
            acc[stepId] = comments;
            return acc;
          }, {} as Record<number, ActionInstanceStepComment[]>);
          setStepComments(stepCommentsMap);
        }
      } catch (error) {
        // console.error('Failed to fetch comments for print', error);
      }
    };

    fetchComments();
  }, [instance.id, instance.steps, showInstanceComments, showStepComments]);

  const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
  const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
  const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
  const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

  const statusData = useMemo(() => {
    let StatusIconComponent: React.ComponentType<{ className?: string }>;
    let statusIconClass: string;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
    }

    return {
      StatusIconComponent,
      statusIconClass,
      displayValue: instance.status,
    };
  }, [instance.status, styles]);

  const criticalityData = useMemo(() => {
    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
    let CriticalityIconComponent: React.ComponentType<{ className?: string }>;
    let criticalityIconClass: string;

    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
    }

    return {
      CriticalityIconComponent,
      criticalityIconClass,
      displayValue: t(action_library_messages.criticality[criticalityKey]),
    };
  }, [instance.criticality, t, styles]);

  const StatusIcon = statusData.StatusIconComponent;
  const CriticalityIcon = criticalityData.CriticalityIconComponent;

  return (
    <div className={styles.root}>
      {showTitle && (
        <Typography className={styles.title} variant='h3'>
          {instance.title}
        </Typography>
      )}

      {showDescription && (
        <div className={styles.section}>
          <div className={styles.value}>{instance.description ? <RichTextContent content={instance.description} /> : '-'}</div>
        </div>
      )}

      {showMetadata && (
        <Grid container spacing={2}>
          {/* Row 1: Status | Criticality */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.status)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                <div className={styles.valueWithIcon}>
                  <StatusIcon className={`${styles.statusIcon} ${statusData.statusIconClass}`} />
                  <span>{instance.statusDisplay || statusData.displayValue}</span>
                </div>
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.criticality)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                <div className={styles.valueWithIcon}>
                  <CriticalityIcon className={`${styles.statusIcon} ${criticalityData.criticalityIconClass}`} />
                  <span>{criticalityData.displayValue}</span>
                </div>
              </Typography>
            </div>
          </Grid>
          {/* Row 2: Template Name | Template Source */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.template_name)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.templateName ?? '–'}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.template_source)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.templateSourceDisplay ?? instance.templateSource ?? '–'}
              </Typography>
            </div>
          </Grid>
          {/* Row 3: Asset Name | Asset Type */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.asset_name)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.assetName || '–'}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.asset_type)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.assetType || '–'}
              </Typography>
            </div>
          </Grid>
          {/* Row 4: Asset Importance | Owner */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.asset_importance)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.assetImportance
                  ? // @ts-ignore
                    t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
                  : '–'}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.owner)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.ownerFullName || '–'}
              </Typography>
            </div>
          </Grid>
          {/* Row 5: Reported To | Due date */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.reported_to)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.reportedTo ?? '–'}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.deadline)}
              </Typography>
              <Typography
                className={styles.value}
                style={{ color: isDeadlinePast ? '#f44336' : 'inherit', fontWeight: isDeadlinePast ? 'bold' : 'normal' }}
                variant='body1'
              >
                {deadlineDate ? format(deadlineDate, 'yyyy-MM-dd HH:mm') : '–'}
              </Typography>
            </div>
          </Grid>
          {/* Row 6: Reminder | Validation Period */}
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.reminder)}
              </Typography>
              <Typography className={styles.value} style={{ color: isReminderPast ? '#ff9800' : 'inherit' }} variant='body1'>
                {reminderDate ? (
                  <div className={styles.valueWithIcon}>
                    <NotificationsIcon style={{ fontSize: '1rem', color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)' }} />
                    <span>{format(reminderDate, 'yyyy-MM-dd HH:mm')}</span>
                  </div>
                ) : (
                  '–'
                )}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.validation_period)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.validityPeriodMonths != null
                  ? t(action_library_messages.instance.detail.validation_period_months, { count: instance.validityPeriodMonths })
                  : '–'}
              </Typography>
            </div>
          </Grid>
        </Grid>
      )}

      {showSpecialInstruction && instance.specialInstruction && (
        <>
          <div className={styles.divider} />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.special_instruction)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              <RichTextContent content={instance.specialInstruction} />
            </Typography>
          </div>
        </>
      )}

      {showInstanceComments && (instance.completionComment || instanceComments.length > 0) && (
        <>
          <div className={styles.divider} />
          <div className={styles.section}>
            <Typography className={styles.sectionTitle} variant='h5'>
              {t(action_library_messages.instance_actions.instance_comments)}
            </Typography>

            {instance.completionComment && (
              <div style={{ marginBottom: 16 }}>
                <Typography style={{ fontWeight: 'bold' }} variant='subtitle2'>
                  {t(action_library_messages.instance.detail.completion_comment)}:
                </Typography>
                <Typography variant='body2'>{instance.completionComment}</Typography>
              </div>
            )}

            {instanceComments.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {instanceComments.map(comment => (
                  <li key={comment.id} style={{ marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 8 }}>
                    <Typography color='textSecondary' style={{ display: 'block', marginBottom: 2 }} variant='caption'>
                      {comment.userFullName || comment.userEmail} - {format(new Date(comment.createdAt), 'yyyy-MM-dd HH:mm')}
                    </Typography>
                    <Typography variant='body2'>{comment.text}</Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {showSteps && instance.steps && instance.steps.length > 0 && (
        <>
          <div className={styles.divider} />
          <div className={styles.section}>
            <Typography className={styles.sectionTitle} variant='h5'>
              {t(action_library_messages.instance.detail.steps)}
            </Typography>
            {[...instance.steps]
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((step, index) => (
                <div key={step.id} className={styles.stepContainer}>
                  <Typography className={styles.stepTitle} variant='subtitle1'>
                    {index + 1}. {step.title}
                    {step.mandatory && <span className={styles.mandatoryBadge}>Mandatory</span>}
                    <span className={styles.stepStatus}>{step.statusDisplay || step.status || '-'}</span>
                  </Typography>
                  <div className={styles.stepContent}>
                    <RichTextContent content={step.instruction} />
                  </div>
                  {showStepComments && stepComments[step.id] && stepComments[step.id].length > 0 && (
                    <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
                      <Typography style={{ fontWeight: 'bold' }} variant='caption'>
                        {t(action_library_messages.instance_actions.step_comments)}:
                      </Typography>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {stepComments[step.id].map(comment => (
                          <li key={comment.id} style={{ fontSize: '0.75rem', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600 }}>
                              {comment.userFullName || comment.userEmail} ({format(new Date(comment.createdAt), 'yyyy-MM-dd HH:mm')}):{' '}
                            </span>
                            {comment.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}

      <div className={styles.footer}>
        <div>
          {t(print_template_messages.footer.printed_by)}: {currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : '-'}
        </div>
        <div>
          {t(print_template_messages.footer.printed_on)}: {format(new Date(), 'yyyy-MM-dd HH:mm')}
        </div>
      </div>
    </div>
  );
};

export default ActionLibraryInstancePrintTemplate;
